export type BrowserQueryKey = readonly unknown[];

export interface BrowserQueryCacheReadOptions {
  staleTimeMs?: number;
  cacheTimeMs?: number;
  tags?: string[];
  invalidateTagsOnFreshFetch?: string[];
}

export interface BrowserQueryCacheReadResult<T> {
  value: T;
  fromCache: boolean;
}

interface PendingEntry<T> {
  kind: 'pending';
  token: symbol;
  promise: Promise<BrowserQueryCacheReadResult<T>>;
  tags: string[];
  staleAt: number;
  cacheExpiresAt: number;
}

interface ResolvedEntry<T> {
  kind: 'resolved';
  token: symbol;
  value: T;
  tags: string[];
  staleAt: number;
  cacheExpiresAt: number;
}

type CacheEntry<T> = PendingEntry<T> | ResolvedEntry<T>;

const defaultStaleTimeMs = 5 * 60 * 1000;
const defaultCacheTimeMs = 30 * 60 * 1000;

function normalizeQueryKey(key: BrowserQueryKey | string): string {
  return typeof key === 'string' ? key : JSON.stringify(key);
}

function uniqueTags(tags?: string[]): string[] {
  return [...new Set((tags ?? []).filter((tag) => tag.trim().length > 0))];
}

export class BrowserQueryCache {
  private readonly entries = new Map<string, CacheEntry<unknown>>();

  private readonly tagIndex = new Map<string, Set<string>>();

  constructor(
    private readonly defaults: {
      staleTimeMs?: number;
      cacheTimeMs?: number;
    } = {},
  ) {}

  async read<T>(
    key: BrowserQueryKey | string,
    fetcher: () => Promise<T>,
    options: BrowserQueryCacheReadOptions = {},
  ): Promise<BrowserQueryCacheReadResult<T>> {
    const cacheKey = normalizeQueryKey(key);
    const now = Date.now();
    const existing = this.entries.get(cacheKey) as CacheEntry<T> | undefined;
    const staleTimeMs = options.staleTimeMs ?? this.defaults.staleTimeMs ?? defaultStaleTimeMs;
    const cacheTimeMs = options.cacheTimeMs ?? this.defaults.cacheTimeMs ?? defaultCacheTimeMs;
    const tags = uniqueTags(options.tags);

    if (existing?.kind === 'resolved') {
      if (existing.cacheExpiresAt <= now) {
        this.deleteEntry(cacheKey, existing.tags);
      } else if (existing.staleAt > now) {
        return { value: existing.value, fromCache: true };
      }
    }

    if (existing?.kind === 'pending') {
      return existing.promise;
    }

    const token = Symbol(cacheKey);
    const pendingEntry: PendingEntry<T> = {
      kind: 'pending',
      token,
      promise: Promise.resolve().then(async () => {
        const value = await fetcher();
        const fetchedAt = Date.now();
        const resolvedEntry: ResolvedEntry<T> = {
          kind: 'resolved',
          token,
          value,
          tags,
          staleAt: fetchedAt + staleTimeMs,
          cacheExpiresAt: fetchedAt + cacheTimeMs,
        };

        if (this.entries.get(cacheKey)?.token === token) {
          this.storeEntry(cacheKey, resolvedEntry);
        }

        for (const tag of uniqueTags(options.invalidateTagsOnFreshFetch)) {
          this.invalidateTag(tag);
        }

        return { value, fromCache: false };
      }),
      tags,
      staleAt: now + staleTimeMs,
      cacheExpiresAt: now + cacheTimeMs,
    };

    this.storeEntry(cacheKey, pendingEntry);

    try {
      return await pendingEntry.promise;
    } catch (error) {
      const currentEntry = this.entries.get(cacheKey);

      if (currentEntry?.token === token) {
        this.deleteEntry(cacheKey, currentEntry.tags);
      }

      throw error;
    }
  }

  peek<T>(key: BrowserQueryKey | string): T | undefined {
    const entry = this.entries.get(normalizeQueryKey(key));

    if (!entry || entry.kind !== 'resolved' || entry.staleAt <= Date.now()) {
      return undefined;
    }

    return entry.value as T;
  }

  invalidate(key: BrowserQueryKey | string): void {
    const cacheKey = normalizeQueryKey(key);
    const entry = this.entries.get(cacheKey);

    if (!entry) {
      return;
    }

    this.deleteEntry(cacheKey, entry.tags);
  }

  invalidateTag(tag: string): void {
    const normalizedTag = tag.trim();

    if (!normalizedTag) {
      return;
    }

    const keys = [...(this.tagIndex.get(normalizedTag) ?? new Set<string>())];

    for (const key of keys) {
      const entry = this.entries.get(key);

      if (entry) {
        this.deleteEntry(key, entry.tags);
      } else {
        this.detachKeyFromTags(key, [normalizedTag]);
      }
    }
  }

  clear(): void {
    this.entries.clear();
    this.tagIndex.clear();
  }

  private storeEntry(key: string, entry: CacheEntry<unknown>): void {
    const previousEntry = this.entries.get(key);

    if (previousEntry) {
      this.detachKeyFromTags(key, previousEntry.tags);
    }

    this.entries.set(key, entry);
    this.attachKeyToTags(key, entry.tags);
  }

  private deleteEntry(key: string, tags: string[]): void {
    this.entries.delete(key);
    this.detachKeyFromTags(key, tags);
  }

  private attachKeyToTags(key: string, tags: string[]): void {
    for (const tag of tags) {
      const entries = this.tagIndex.get(tag) ?? new Set<string>();
      entries.add(key);
      this.tagIndex.set(tag, entries);
    }
  }

  private detachKeyFromTags(key: string, tags: string[]): void {
    for (const tag of tags) {
      const entries = this.tagIndex.get(tag);

      if (!entries) {
        continue;
      }

      entries.delete(key);

      if (entries.size === 0) {
        this.tagIndex.delete(tag);
      }
    }
  }
}

const defaultBrowserQueryCache = new BrowserQueryCache();

export function createBrowserQueryCache(): BrowserQueryCache {
  return new BrowserQueryCache();
}

export function getDefaultBrowserQueryCache(): BrowserQueryCache {
  return defaultBrowserQueryCache;
}

export function resolveBrowserQueryCache(cache?: BrowserQueryCache): BrowserQueryCache {
  return cache ?? defaultBrowserQueryCache;
}

export function serializeBrowserQueryKey(key: BrowserQueryKey): string {
  return normalizeQueryKey(key);
}
